import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { LockIcon, SignOutIcon } from '@phosphor-icons/react';

import { Button } from '../../../components/button/Button';
import { Card } from '../../../components/Card';
import { DataField } from '../../../components/DataField';
import { InputWrapper } from '../../../components/InputWrapper';
import { PageHeader } from '../../../components/PageHeader';
import { Pill } from '../../../components/pill/Pill';
import { SimpleSelect } from '../../../components/select/SimpleSelect';
import { useAuth } from '../../../contexts/auth-context';
import { Language } from 'src/types/enums';
import { formatDateTime } from '../../../utils/date';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { getDisplayError } from '../../../utils/get-display-error';
import { Input } from '../../../components/input/Input';
import { PageHeading } from 'components/PageHeading';
import { Breadcrumb } from 'components/Breadcrumb';
import { captureException } from '@sentry/react';
import { updateMyProfile } from '../models';
import { changePassword } from '../../auth/models';

export interface IMePageProps {}

export const MePage: React.FC<IMePageProps> = () => {
  const { me, logout, refreshMe } = useAuth();
  const [nameValue, setNameValue] = useState(me.name);

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  useEffect(() => {
    setNameValue(me.name);
  }, [me.name]);

  const persistPasswordChange = async () => {
    if (newPassword.length < 12) {
      toast.error('Het nieuwe wachtwoord moet minstens 12 tekens lang zijn');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('De wachtwoorden komen niet overeen');
      return;
    }

    setIsChangingPassword(true);
    try {
      await changePassword(oldPassword, newPassword);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Wachtwoord is bijgewerkt');
    } catch (err) {
      captureException(err);
      console.log(err);
      toast.error('Kon wachtwoord niet bijwerken: ' + getDisplayError(err));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const persistNameChange = async () => {
    if (!nameValue.trim()) return;

    try {
      await updateMyProfile({
        name: nameValue.trim(),
      });
      refreshMe();
      toast.success('Naam is bijgewerkt');
    } catch (err) {
      captureException(err);
      console.log(err);
      toast.error('Kon naam niet bijwerken: ' + getDisplayError(err));
    }
  };

  const initial = me.name.trim().charAt(0).toUpperCase() || 'T';

  return (
    <>
      <PageHeader title="Mijn profiel" />

      <PageHeading
        noMarginBottom
        leftSide={<Breadcrumb currentItem="Mijn profiel" />}
        rightSide={
          <Button color="danger" iconLeft={<SignOutIcon className="button-icon" />} onTrigger={() => logout()}>
            Log uit
          </Button>
        }
      />

      <div className="p-7">
        <div className="flex max-w-[760px] flex-col gap-4">
          <Card title="Profiel">
            <div className="flex gap-6">
              {me.image ? (
                <img
                  src={me.image}
                  alt={me.name}
                  className="h-24 w-24 shrink-0 rounded-lg border border-border object-cover"
                />
              ) : (
                <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg border border-brand-border bg-brand-subtle text-4xl font-semibold text-brand-text">
                  {initial}
                </div>
              )}

              <div className="grid min-w-0 flex-1 grid-cols-2 gap-x-4 gap-y-[18px]">
                <InputWrapper labelText="Naam" helperText="Wordt bewaard zodra je het veld verlaat.">
                  <Input
                    type="text"
                    value={nameValue}
                    onChange={(newValue) => {
                      setNameValue(newValue);
                    }}
                    onBlur={() => {
                      persistNameChange();
                    }}
                    onKeyDown={(evt) => {
                      if (evt.key === 'Enter') {
                        persistNameChange();
                      }
                    }}
                  />
                </InputWrapper>

                <InputWrapper labelText="Taal" helperText="Taal van de interface en van uitgaande documenten.">
                  <SimpleSelect
                    items={LANGUAGE_VALUES}
                    selectedItem={LANGUAGE_VALUES.find((v) => v.key === me.language) ?? null}
                    onSelect={async (newValue) => {
                      try {
                        await updateMyProfile({
                          language: newValue?.key as Language,
                        });
                        refreshMe();
                        toast.success('Taal is bijgewerkt');
                      } catch (err) {
                        captureException(err);
                        console.log(err);
                        toast.error('Kon taal niet bijwerken: ' + getDisplayError(err));
                      }
                    }}
                  />
                </InputWrapper>

                <DataField title="E-mail">
                  <div className="flex h-9 items-center truncate font-medium">{me.email}</div>
                </DataField>

                {me.isInternal && me.role && (
                  <DataField title="Rol">
                    <div className="flex h-9 items-center">
                      <Pill>{me.role}</Pill>
                    </div>
                  </DataField>
                )}

                <DataField title="Lid geworden op">
                  <div className="flex h-9 items-center font-medium">{formatDateTime(me.createdAt)}</div>
                </DataField>
              </div>
            </div>
          </Card>

          {!me.isInternal && (
            <Card title="Wachtwoord wijzigen" subtitle="Kies een wachtwoord van minstens 12 tekens.">
              <div className="flex flex-col gap-[18px]">
                <div className="grid grid-cols-2 gap-x-4">
                  <InputWrapper labelText="Huidig wachtwoord" hideInvalid>
                    <Input type="password" value={oldPassword} onChange={(newValue) => setOldPassword(newValue)} />
                  </InputWrapper>
                </div>

                <div className="grid grid-cols-2 gap-x-4">
                  <InputWrapper labelText="Nieuw wachtwoord" hideInvalid>
                    <Input type="password" value={newPassword} onChange={(newValue) => setNewPassword(newValue)} />
                  </InputWrapper>

                  <InputWrapper labelText="Bevestig nieuw wachtwoord" hideInvalid>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(newValue) => setConfirmPassword(newValue)}
                    />
                  </InputWrapper>
                </div>

                <div>
                  <Button
                    color="primary"
                    iconLeft={<LockIcon className="button-icon" />}
                    isLoading={isChangingPassword}
                    isDisabled={!oldPassword || !newPassword || !confirmPassword}
                    onTrigger={() => persistPasswordChange()}
                  >
                    Wachtwoord wijzigen
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
};
